import React, {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { getModalFocusRoot } from "./AnchoredMenu";

export type MonthDayCalendarValue = {
  month: string;
  day: string;
};

export const calendarMonthOptions = [
  { label: "January", shortLabel: "Jan", days: 31 },
  { label: "February", shortLabel: "Feb", days: 29 },
  { label: "March", shortLabel: "Mar", days: 31 },
  { label: "April", shortLabel: "Apr", days: 30 },
  { label: "May", shortLabel: "May", days: 31 },
  { label: "June", shortLabel: "Jun", days: 30 },
  { label: "July", shortLabel: "Jul", days: 31 },
  { label: "August", shortLabel: "Aug", days: 31 },
  { label: "September", shortLabel: "Sep", days: 30 },
  { label: "October", shortLabel: "Oct", days: 31 },
  { label: "November", shortLabel: "Nov", days: 30 },
  { label: "December", shortLabel: "Dec", days: 31 },
];

type CalendarDateInputBaseProps = {
  ariaLabel: string;
  ariaInvalid?: boolean;
  ariaDescribedBy?: string;
  // Red field border for a missing or invalid date (Claude Design (8)
  // ds-components/DateField.jsx:174). Also sets aria-invalid on the editable
  // full-date input.
  error?: boolean;
  // Text shown under the field with the red border, so a missing or wrong
  // date is not shown by colour only (audit 3.2 item 4).
  errorMessage?: string;
  className?: string;
  // Shown when a fullDate value is empty (e.g. unset filters) instead of
  // falling back to today's date.
  placeholder?: string;
  placeholderClassName?: string;
  disabled?: boolean;
  inputClassName?: string;
  displayFormat?: "short" | "long";
  // Every calendar uses the Claude Design DateField look (IF10 D15). The old
  // "default" look is removed; the prop stays so existing callers compile.
  appearance?: "claude";
  // "dropdown" is the Claude Design DateField header (centred ‹ Month › and a
  // year button that opens a ±10 year list). It is the default; callers that
  // pass "arrows" keep that control.
  yearNavigation?: "arrows" | "dropdown";
  // Year list of the dropdown header. "design" is the Claude Design ±10 list.
  // "historical" is for past-date fields (Became A Member, Member Referral,
  // Affinity Start, notes filters): 1900 up to next year, so old records can
  // be entered without many clicks (Monday audit Oct 19 2026, section F).
  yearRange?: CalendarDropdownYearRange;
  calendarVariant?: "date" | "range";
  calendarContainer?: HTMLElement | null;
  // Visual focus for an empty filter is not a selected date or a filter value.
  highlightTodayWhenEmpty?: boolean;
  minDate?: string;
  maxDate?: string;
  // Lets form wrappers associate a <label htmlFor> with the field trigger.
  id?: string;
  // Side of the field the calendar icon sits on. Defaults to the original
  // right-side placement used across Chamber Settings; Members forms render
  // it on the left per the member-module designs (MEM-CREATE-008).
  iconPosition?: "left" | "right";
};

type CalendarDateInputProps =
  | (CalendarDateInputBaseProps & {
      mode: "monthDay";
      value: MonthDayCalendarValue;
      onChange: (value: MonthDayCalendarValue) => void;
    })
  | (CalendarDateInputBaseProps & {
      mode: "fullDate";
      value: string;
      // Valid text is normalized to YYYY-MM-DD. Partial or invalid text stays
      // in the controlled draft so consumers can validate without losing it.
      onChange: (value: string) => void;
    });

const claudeWeekdayLabels = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const rangeWeekdayLabels = ["M", "T", "W", "T", "F", "S", "S"];
const minimumCalendarYear = 1900;
const futureCalendarYearCount = 25;
// Claude Design (19) ds-components/DateField.jsx: the year list shows the
// current year ±10.
const dropdownYearSpan = 10;

export const getCalendarYearOptions = ({
  currentYear = new Date().getFullYear(),
  visibleYear,
}: {
  currentYear?: number;
  visibleYear: number;
}) => {
  const maximumCalendarYear = currentYear + futureCalendarYearCount;
  const years = Array.from(
    { length: maximumCalendarYear - minimumCalendarYear + 1 },
    (_, index) => minimumCalendarYear + index,
  );

  if (!years.includes(visibleYear)) {
    years.push(visibleYear);
    years.sort((left, right) => left - right);
  }

  return years;
};

export type CalendarDropdownYearRange = "design" | "historical";

// A saved date outside the year window stays in the list so the visible year
// is always selectable.
export const getCalendarDropdownYearOptions = ({
  currentYear = new Date().getFullYear(),
  visibleYear,
  yearRange = "design",
}: {
  currentYear?: number;
  visibleYear: number;
  yearRange?: CalendarDropdownYearRange;
}) => {
  const firstYear =
    yearRange === "historical"
      ? minimumCalendarYear
      : currentYear - dropdownYearSpan;
  const lastYear =
    yearRange === "historical" ? currentYear + 1 : currentYear + dropdownYearSpan;
  const years = Array.from(
    { length: lastYear - firstYear + 1 },
    (_, index) => firstYear + index,
  );

  if (!years.includes(visibleYear)) {
    years.push(visibleYear);
    years.sort((left, right) => left - right);
  }

  return years;
};

const getMonthIndex = (month: string) => {
  const monthIndex = calendarMonthOptions.findIndex(
    ({ label }) => label === month
  );

  return monthIndex >= 0 ? monthIndex : 0;
};

const clampDayForMonth = (monthIndex: number, day: string) => {
  const parsedDay = Number(day) || 1;
  const maxDay = calendarMonthOptions[monthIndex].days;

  return String(Math.min(Math.max(parsedDay, 1), maxDay));
};

const parseDateInputValue = (value: string) => {
  const [year, month, day] = value.split("-").map(Number);

  if (!year || !month || !day) {
    return new Date();
  }

  const date = new Date(year, month - 1, day, 12, 0, 0, 0);

  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return new Date();
  }

  return date;
};

const toDateInputValue = ({
  year,
  monthIndex,
  day,
}: {
  year: number;
  monthIndex: number;
  day: number;
}) => {
  const paddedMonth = String(monthIndex + 1).padStart(2, "0");
  const paddedDay = String(day).padStart(2, "0");

  return `${year}-${paddedMonth}-${paddedDay}`;
};

// Editable dates accept only the two displayed forms. Date.parse can silently
// move an impossible date into the next month and would hide validation errors.
const parseEditableDateValue = (value: string): Date | null => {
  const trimmedValue = value.trim();
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmedValue);
  const longMatch = /^([a-z]+)\s+(\d{1,2}),\s*(\d{4})$/i.exec(trimmedValue);
  const match = isoMatch ?? longMatch;

  if (!match) {
    return null;
  }

  const year = Number(match[isoMatch ? 1 : 3]);
  const monthIndex = isoMatch
    ? Number(isoMatch[2]) - 1
    : calendarMonthOptions.findIndex(
        ({ label }) => label.toLowerCase() === match[1].toLowerCase(),
      );
  const day = Number(match[isoMatch ? 3 : 2]);
  const date = new Date(year, monthIndex, day, 12, 0, 0, 0);

  return date.getFullYear() === year &&
    date.getMonth() === monthIndex &&
    date.getDate() === day
    ? date
    : null;
};

// True for a complete, real calendar date in the YYYY-MM-DD storage form.
// Callers use it to reject partial typed text before save.
export const isCalendarDateValue = (value: string): boolean => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const date = new Date(year, month - 1, day, 12, 0, 0, 0);
  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
};

const formatFullDateValue = (
  value: string,
  displayFormat: "short" | "long" = "short",
) => {
  const date = parseDateInputValue(value);

  return new Intl.DateTimeFormat("en-US", {
    month: displayFormat,
    day: "numeric",
    year: "numeric",
  }).format(date);
};

// Claude Design (8) ds-components/DateField.jsx:8 (LINE): input, month
// buttons and calendar use the #9E9E9E input border.
const claudeDateInputClassName =
  "h-[43px] w-full rounded-lg border border-[#9E9E9E] bg-white py-[11px] pl-[14px] pr-[38px] text-[14px] leading-[19px] text-[#0A0A0A] outline-none focus:border-[#E1A731] disabled:cursor-not-allowed disabled:opacity-50";

const claudeNavigationButtonStyle: React.CSSProperties = {
  alignItems: "center",
  background: "#FFFFFF",
  border: "1px solid #9E9E9E",
  borderRadius: 6,
  color: "#0A0A0A",
  display: "inline-flex",
  fontSize: 16,
  height: 28,
  justifyContent: "center",
  lineHeight: 1,
  padding: 0,
  width: 28,
};

type CalendarPopoverPosition = {
  fontFamily: string;
  left: number;
  top: number;
};

const CalendarHeaderSelect = ({
  ariaLabel,
  value,
  options,
  onChange,
  width,
}: {
  ariaLabel: string;
  value: number;
  options: { label: string; value: number }[];
  onChange: (value: number) => void;
  width?: number;
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  useEffect(() => {
    if (!open) return;
    const outside = (event: MouseEvent) => {
      if (event.target instanceof Node && !rootRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", outside);
    const selected = listRef.current?.querySelector<HTMLButtonElement>('[aria-selected="true"]');
    selected?.focus();
    selected?.scrollIntoView?.({ block: "nearest" });
    return () => document.removeEventListener("mousedown", outside);
  }, [open]);
  return (
    <div ref={rootRef} style={{ position: "relative", flex: width ? `0 0 ${width}px` : 1, minWidth: 0 }}>
      <button
        ref={triggerRef}
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        onClick={() => setOpen((current) => !current)}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            setOpen(true);
          }
        }}
        className="focus-visible:ring-2 focus-visible:ring-cpaGold/40"
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6, width: "100%", fontSize: 13, color: "#0A0A0A", background: "#fff", border: "1px solid #9E9E9E", borderRadius: 8, padding: "8px 10px", outline: "none" }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{options.find((option) => option.value === value)?.label}</span>
        <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#727272" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="m6 9 6 6 6-6" /></svg>
      </button>
      {open && (
        <div
          ref={listRef}
          id={listId}
          role="listbox"
          aria-label={ariaLabel}
          style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0, zIndex: 140, background: "#fff", border: "1px solid #ECECEC", borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,.14)", padding: "6px 0", maxHeight: 208, overflowY: "auto" }}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.preventDefault();
              event.stopPropagation();
              setOpen(false);
              triggerRef.current?.focus();
              return;
            }
            if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
            event.preventDefault();
            const buttons = Array.from(listRef.current?.querySelectorAll<HTMLButtonElement>('[role="option"]') || []);
            const current = buttons.indexOf(document.activeElement as HTMLButtonElement);
            const next = event.key === "Home" ? 0 : event.key === "End" ? buttons.length - 1 : (current + (event.key === "ArrowDown" ? 1 : -1) + buttons.length) % buttons.length;
            buttons[next]?.focus();
          }}
        >
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={option.value === value}
              tabIndex={-1}
              onClick={() => {
                onChange(option.value);
                setOpen(false);
                triggerRef.current?.focus();
              }}
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, width: "100%", fontSize: 13, textAlign: "left", border: "none", padding: "9px 12px", background: option.value === value ? "rgba(225,167,49,.14)" : "transparent", color: "#0A0A0A", fontWeight: option.value === value ? 600 : 400 }}
            >
              <span>{option.label}</span>
              {option.value === value && <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#E1A731" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="m5 12.5 4.5 4.5L19 7" /></svg>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

type CalendarYearDropdownProps = {
  open: boolean;
  visibleYear: number;
  years: number[];
  isYearDisabled: (year: number) => boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (year: number) => void;
};

// Claude Design (19) ds-components/DateField.jsx year button and 110px list.
const CalendarYearDropdown = ({
  open,
  visibleYear,
  years,
  isYearDisabled,
  onOpenChange,
  onSelect,
}: CalendarYearDropdownProps) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const listId = useId();

  useEffect(() => {
    const list = listRef.current;
    if (!open || !list) return;
    const current = list.querySelector<HTMLButtonElement>('[aria-selected="true"]');
    if (!current) return;
    list.scrollTop = Math.max(
      0,
      current.offsetTop - list.clientHeight / 2 + current.offsetHeight / 2,
    );
    current.focus({ preventScroll: true });
  }, [open]);

  const closeList = () => {
    onOpenChange(false);
    triggerRef.current?.focus();
  };

  return (
    <div className="relative ml-[4px] flex items-center">
      <button
        ref={triggerRef}
        type="button"
        aria-label="Select year"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        onClick={() => onOpenChange(!open)}
        className="inline-flex items-center gap-[6px] rounded-[6px] border border-[#9E9E9E] bg-white py-[3px] pl-[10px] pr-[8px] text-[14px] font-semibold leading-[1.2] text-[#0A0A0A] focus:outline-none focus-visible:ring-2 focus-visible:ring-cpaGold/40"
      >
        {visibleYear}
        <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="#727272" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 13, height: 13, transform: open ? "rotate(180deg)" : "none" }}>
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {open && (
        <div
          ref={listRef}
          id={listId}
          role="listbox"
          aria-label="Select year"
          className="absolute left-1/2 top-full z-[20] mt-[4px] flex max-h-[200px] w-[110px] -translate-x-1/2 flex-col overflow-y-auto rounded-[8px] border border-[#9E9E9E] bg-white p-[4px]"
          style={{ boxShadow: "0 8px 22px rgba(0,0,0,.14)" }}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              // Close only the year list; the calendar stays open.
              event.preventDefault();
              event.stopPropagation();
              closeList();
              return;
            }
            if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
            event.preventDefault();
            const options = Array.from(
              listRef.current?.querySelectorAll<HTMLButtonElement>('[role="option"]:not(:disabled)') || [],
            );
            const current = options.indexOf(document.activeElement as HTMLButtonElement);
            const next = event.key === "Home"
              ? 0
              : event.key === "End"
              ? options.length - 1
              : (current + (event.key === "ArrowDown" ? 1 : -1) + options.length) % options.length;
            options[next]?.focus();
          }}
        >
          {years.map((year) => {
            const selected = year === visibleYear;
            const disabled = isYearDisabled(year);

            return (
              <button
                key={year}
                type="button"
                role="option"
                aria-selected={selected}
                disabled={disabled || undefined}
                tabIndex={-1}
                onClick={() => {
                  onSelect(year);
                  closeList();
                }}
                className={`shrink-0 rounded-[6px] border-0 px-[10px] py-[7px] text-center text-[13px] leading-[normal] focus:outline-none focus-visible:ring-2 focus-visible:ring-cpaGold/40 ${
                  selected
                    ? "bg-[#E1A731] font-bold text-white"
                    : disabled
                    ? "cursor-default bg-transparent font-normal text-[#C9C9C9]"
                    : "bg-transparent font-normal text-[#0A0A0A] hover:bg-[rgba(225,167,49,.12)]"
                }`}
              >
                {year}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const calendarIcon = (
  <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M8 3v4M16 3v4M3 11h18" />
  </svg>
);

const CalendarDateInput = (props: CalendarDateInputProps) => {
  const {
    ariaLabel,
    ariaInvalid,
    ariaDescribedBy,
    error = false,
    errorMessage,
    className = "",
    disabled = false,
    displayFormat = "short",
    yearNavigation = "dropdown",
    yearRange = "design",
    calendarVariant = "date",
    calendarContainer,
    minDate,
    maxDate,
    placeholderClassName = "text-[#9E9E9E]",
    inputClassName = claudeDateInputClassName,
    id,
    iconPosition = "right",
    mode,
  } = props;
  const isRange = calendarVariant === "range";
  const isInlineCalendar = isRange && Boolean(calendarContainer);
  const calendarPopoverId = useId();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const [popoverPosition, setPopoverPosition] =
    useState<CalendarPopoverPosition | null>(null);
  // The portal renders inside an enclosing MUI Dialog/Menu focus-trap root,
  // so the dialog does not pull focus back out of the calendar.
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(
    null,
  );
  const [open, setOpen] = useState(false);
  const [yearListOpen, setYearListOpen] = useState(false);
  // A typed date outside minDate/maxDate is shown with an error but is never
  // sent to the caller. It clears when the field loses focus.
  const [outOfRangeText, setOutOfRangeText] = useState<string | null>(null);
  const [visibleMonthIndex, setVisibleMonthIndex] = useState(() =>
    mode === "monthDay"
      ? getMonthIndex(props.value.month)
      : new Date().getMonth()
  );
  const [visibleYear, setVisibleYear] = useState(() =>
    new Date().getFullYear()
  );
  const calendarYearOptions = useMemo(
    () => getCalendarYearOptions({ visibleYear }),
    [visibleYear],
  );
  const dropdownYearOptions = useMemo(
    () => getCalendarDropdownYearOptions({ visibleYear, yearRange }),
    [visibleYear, yearRange],
  );
  const toBoundValue = (bound?: string) => {
    const date = bound ? parseEditableDateValue(bound) : null;

    return date
      ? toDateInputValue({
          year: date.getFullYear(),
          monthIndex: date.getMonth(),
          day: date.getDate(),
        })
      : null;
  };
  const minimumDateValue = toBoundValue(minDate);
  const maximumDateValue = toBoundValue(maxDate);
  const isOutsideDateBounds = (value: string) =>
    Boolean(
      (minimumDateValue && value < minimumDateValue) ||
        (maximumDateValue && value > maximumDateValue),
    );
  // A year is unavailable only when every day in it is outside the bounds.
  const isYearOutsideDateBounds = (year: number) =>
    Boolean(
      (minimumDateValue && year < Number(minimumDateValue.slice(0, 4))) ||
        (maximumDateValue && year > Number(maximumDateValue.slice(0, 4))),
    );
  const outOfRangeMessage = outOfRangeText === null
    ? ""
    : minimumDateValue && maximumDateValue
      ? `Select a date from ${formatFullDateValue(minimumDateValue, "long")} to ${formatFullDateValue(maximumDateValue, "long")}.`
      : minimumDateValue
        ? `Select a date on or after ${formatFullDateValue(minimumDateValue, "long")}.`
        : maximumDateValue
          ? `Select a date on or before ${formatFullDateValue(maximumDateValue, "long")}.`
          : "";
  const shownErrorMessage = outOfRangeMessage || (error ? errorMessage?.trim() || "" : "");
  const errorMessageId = useId();
  const describedBy = [ariaDescribedBy, shownErrorMessage ? errorMessageId : ""]
    .filter(Boolean)
    .join(" ") || undefined;
  const showError = error || outOfRangeText !== null;
  const errorBorderStyle: React.CSSProperties | undefined = showError
    ? { borderColor: "#E24C4C" }
    : undefined;

  useLayoutEffect(() => {
    if (!open || isInlineCalendar) {
      return undefined;
    }

    // Claude Design CLAUDE.md:78: the popover is position:fixed and anchored
    // from the trigger's getBoundingClientRect(), so no scroll container
    // clips it. It uses the field's font because the portal is outside the
    // page's typography.
    const updatePopoverPosition = () => {
      const anchor = rootRef.current;
      const panel = popoverRef.current;

      if (!anchor || !panel) {
        return;
      }

      const anchorBounds = anchor.getBoundingClientRect();
      const panelBounds = panel.getBoundingClientRect();
      const left = Math.max(
        8,
        Math.min(anchorBounds.left, window.innerWidth - panelBounds.width - 8),
      );
      const below = anchorBounds.bottom + 6;
      const top = Math.max(
        8,
        below + panelBounds.height <= window.innerHeight - 8
          ? below
          : anchorBounds.top - panelBounds.height - 6,
      );
      const fontFamily = window.getComputedStyle(anchor).fontFamily;

      setPopoverPosition((current) =>
        current?.left === left &&
        current.top === top &&
        current.fontFamily === fontFamily
          ? current
          : { left, top, fontFamily },
      );
    };

    updatePopoverPosition();
    window.addEventListener("resize", updatePopoverPosition);
    window.addEventListener("scroll", updatePopoverPosition, true);
    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(updatePopoverPosition);

    if (rootRef.current) resizeObserver?.observe(rootRef.current);
    if (popoverRef.current) resizeObserver?.observe(popoverRef.current);

    return () => {
      window.removeEventListener("resize", updatePopoverPosition);
      window.removeEventListener("scroll", updatePopoverPosition, true);
      resizeObserver?.disconnect();
    };
  }, [isInlineCalendar, open, visibleMonthIndex, visibleYear]);

  useEffect(() => {
    if (!open) {
      setYearListOpen(false);
      return undefined;
    }

    const handleDocumentOutsideInteraction = (event: Event) => {
      if (
        event.target instanceof Node &&
        !rootRef.current?.contains(event.target) &&
        !popoverRef.current?.contains(event.target)
      ) {
        setOpen(false);
      }
    };

    // Fallback for an Escape that no field handler received (for example
    // focus on the page body).
    const handleDocumentKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleDocumentOutsideInteraction);
    document.addEventListener("keydown", handleDocumentKeyDown);
    // Keyboard navigation and dialogs can move focus without a mouse press.
    document.addEventListener("focusin", handleDocumentOutsideInteraction);

    return () => {
      document.removeEventListener("mousedown", handleDocumentOutsideInteraction);
      document.removeEventListener("keydown", handleDocumentKeyDown);
      document.removeEventListener("focusin", handleDocumentOutsideInteraction);
    };
  }, [open]);

  const focusField = () =>
    (mode === "fullDate" ? inputRef.current : triggerRef.current)?.focus();

  const handleOpen = () => {
    if (disabled) {
      return;
    }

    if (mode === "monthDay") {
      setVisibleMonthIndex(getMonthIndex(props.value.month));
    } else {
      const date = parseEditableDateValue(props.value) ?? new Date();
      setVisibleMonthIndex(date.getMonth());
      setVisibleYear(date.getFullYear());
    }

    setPortalContainer(getModalFocusRoot(rootRef.current) ?? document.body);
    setOpen(true);
  };

  // Escape closes only the calendar. It must not reach an enclosing MUI
  // Dialog or menu, which would close too.
  const handleEscapeKey = (event: React.KeyboardEvent) => {
    if (event.key !== "Escape" || !open) return false;
    event.preventDefault();
    event.stopPropagation();
    // Focus first: the input's focus handler opens the calendar, and the
    // close below must be the last state change.
    focusField();
    setOpen(false);
    return true;
  };

  // An empty month-day value shows the placeholder only when one is given
  // (design (11) Multi-Billing Dates have no default dates).
  const isEmptyMonthDay =
    mode === "monthDay" && !props.value.month && !!props.placeholder;
  const isEmptyFullDate =
    (mode === "fullDate" && !props.value) || isEmptyMonthDay;
  const displayValue =
    isEmptyMonthDay
      ? props.placeholder
      : mode === "monthDay"
      ? `${props.value.month || calendarMonthOptions[0].label} ${
          props.value.day || "1"
        }`
      : isEmptyFullDate
      ? props.placeholder || "Select Date"
      : !parseEditableDateValue(props.value)
      ? props.value
      : formatFullDateValue(props.value, displayFormat);

  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (mode !== "fullDate") {
      return;
    }

    const value = event.target.value;
    const date = parseEditableDateValue(value);
    const isoValue = date
      ? toDateInputValue({
          year: date.getFullYear(),
          monthIndex: date.getMonth(),
          day: date.getDate(),
        })
      : null;

    if (isoValue && isOutsideDateBounds(isoValue)) {
      setOutOfRangeText(value);
      return;
    }

    setOutOfRangeText(null);
    props.onChange(isoValue ?? value);
  };

  const handleMonthSelect = (monthIndex: number) => {
    if (mode !== "monthDay") {
      return;
    }

    setVisibleMonthIndex(monthIndex);
    props.onChange({
      month: calendarMonthOptions[monthIndex].label,
      day: clampDayForMonth(monthIndex, props.value.day),
    });
  };

  const handleMonthDaySelect = (day: number) => {
    if (mode !== "monthDay") {
      return;
    }

    props.onChange({
      month: calendarMonthOptions[visibleMonthIndex].label,
      day: String(day),
    });
    triggerRef.current?.focus();
    setOpen(false);
  };

  const moveVisibleMonth = (offset: number) => {
    setYearListOpen(false);
    const nextDate = new Date(visibleYear, visibleMonthIndex + offset, 1);
    setVisibleMonthIndex(nextDate.getMonth());
    setVisibleYear(nextDate.getFullYear());
  };

  const handleFullDateSelect = (day: number) => {
    if (mode !== "fullDate") {
      return;
    }

    const value = toDateInputValue({
      year: visibleYear,
      monthIndex: visibleMonthIndex,
      day,
    });

    if (isOutsideDateBounds(value)) {
      return;
    }

    setOutOfRangeText(null);
    props.onChange(value);
    inputRef.current?.focus();
    setOpen(false);
  };

  // CMP Dashboard (20) month-day picker (CMP Dashboard.dc.html:7903-7914,
  // buildPicker :17655): 3-column month buttons, a divider, then 30px days.
  const renderMonthDayCalendar = () => {
    const selectedMonthIndex =
      mode === "monthDay" ? getMonthIndex(props.value.month) : 0;
    const selectedDay = mode === "monthDay" ? Number(props.value.day) : 1;
    const hasValue = mode === "monthDay" && Boolean(props.value.month);
    const days = calendarMonthOptions[visibleMonthIndex].days;

    return (
      <>
        <div className="grid grid-cols-3 gap-[8px]">
          {calendarMonthOptions.map((month, monthIndex) => {
            const selected = monthIndex === visibleMonthIndex;

            return (
              <button
                key={month.label}
                type="button"
                aria-label={month.label}
                aria-pressed={selected}
                onClick={() => handleMonthSelect(monthIndex)}
                className={`rounded-[8px] border-0 py-[9px] text-[13px] font-semibold leading-[normal] focus:outline-none focus-visible:ring-2 focus-visible:ring-cpaGold/40 ${
                  selected
                    ? "bg-[#E1A731] text-white"
                    : "bg-[#F3F4F6] text-[#0A0A0A] hover:bg-[rgba(225,167,49,.12)]"
                }`}
              >
                {month.shortLabel}
              </button>
            );
          })}
        </div>
        <div className="my-[4px] border-b border-[#ECECEC]" />
        <div className="grid grid-cols-7 gap-[4px]">
          {Array.from({ length: days }, (_, index) => {
            const day = index + 1;
            const selected =
              hasValue &&
              selectedMonthIndex === visibleMonthIndex &&
              selectedDay === day;

            return (
              <button
                key={day}
                type="button"
                aria-label={`${calendarMonthOptions[visibleMonthIndex].label} ${day}`}
                aria-pressed={selected}
                onClick={() => handleMonthDaySelect(day)}
                className={`h-[30px] w-[30px] justify-self-center rounded-[8px] border-0 p-0 text-[13px] font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-cpaGold/40 ${
                  selected
                    ? "bg-[#E1A731] text-white"
                    : "bg-transparent text-[#0A0A0A] hover:bg-[rgba(225,167,49,.12)]"
                }`}
              >
                {day}
              </button>
            );
          })}
        </div>
      </>
    );
  };

  const renderFullDateCalendar = () => {
    const selectedValue = mode === "fullDate" ? props.value : "";
    const daysInMonth = new Date(
      visibleYear,
      visibleMonthIndex + 1,
      0
    ).getDate();
    const firstWeekdayIndex = new Date(visibleYear, visibleMonthIndex, 1).getDay();
    const firstWeekday = isRange ? (firstWeekdayIndex + 6) % 7 : firstWeekdayIndex;
    const blankCells = Array.from(
      { length: firstWeekday },
      (_, index) => index
    );
    const weekdayCells = (isRange ? rangeWeekdayLabels : claudeWeekdayLabels).map(
      (weekday, index) => (
        <div
          key={`${weekday}-${index}`}
          style={{
            color: isRange ? "#9E9E9E" : "#727272",
            fontSize: 11,
            fontWeight: isRange ? 400 : 700,
            lineHeight: "15px",
            padding: "4px 0",
            textAlign: "center",
          }}
        >
          {weekday}
        </div>
      ),
    );
    const dayCells = (
      <>
        {blankCells.map((index) => <div key={`blank-${index}`} />)}
        {Array.from({ length: daysInMonth }, (_, index) => {
          const day = index + 1;
          const value = toDateInputValue({
            year: visibleYear,
            monthIndex: visibleMonthIndex,
            day,
          });
          const selected = selectedValue === value;
          const today = new Date();
          const highlightToday = props.highlightTodayWhenEmpty && !selectedValue &&
            visibleYear === today.getFullYear() &&
            visibleMonthIndex === today.getMonth() && day === today.getDate();
          const isOutsideBounds = isOutsideDateBounds(value);

          return (
            <button
              key={day}
              type="button"
              disabled={isOutsideBounds || undefined}
              onClick={() => handleFullDateSelect(day)}
              aria-label={`${calendarMonthOptions[visibleMonthIndex].label} ${day}, ${visibleYear}`}
              aria-pressed={selected}
              style={isRange ? { height: 30, borderRadius: 7, padding: 0, fontWeight: selected ? 600 : 400 } : undefined}
              className={`rounded-md text-[13px] leading-[17px] py-[6px] focus:outline-none focus-visible:ring-2 focus-visible:ring-cpaGold/40 ${
                isOutsideBounds
                  ? "cursor-default text-[#C9C9C9]"
                  : selected || highlightToday
                  ? "bg-[#E1A731] font-bold text-white"
                  : "bg-transparent font-normal text-[#0A0A0A] hover:bg-[rgba(225,167,49,.12)]"
              }`}
            >
              {day}
            </button>
          );
        })}
      </>
    );

    return (
      <>
        {isRange ? (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <button type="button" aria-label="Previous month" onClick={() => moveVisibleMonth(-1)} style={{ background: "none", border: "none", fontSize: 17, color: "#0A0A0A", padding: "0 4px", lineHeight: 1 }}>‹</button>
            <CalendarHeaderSelect ariaLabel={`${ariaLabel} month`} value={visibleMonthIndex} options={calendarMonthOptions.map((month, index) => ({ label: month.label, value: index }))} onChange={setVisibleMonthIndex} />
            <CalendarHeaderSelect ariaLabel={`${ariaLabel} year`} value={visibleYear} options={calendarYearOptions.map((year) => ({ label: String(year), value: year }))} onChange={setVisibleYear} width={86} />
            <button type="button" aria-label="Next month" onClick={() => moveVisibleMonth(1)} style={{ background: "none", border: "none", fontSize: 17, color: "#0A0A0A", padding: "0 4px", lineHeight: 1 }}>›</button>
          </div>
        ) : yearNavigation === "dropdown" ? (
          <div className="flex items-center justify-center gap-[8px]" data-calendar-header="dropdown">
            <button
              type="button"
              onClick={() => moveVisibleMonth(-1)}
              className="focus:outline-none focus-visible:ring-2 focus-visible:ring-cpaGold/40"
              style={claudeNavigationButtonStyle}
              aria-label="Previous month"
            >
              ‹
            </button>
            <span className="min-w-[78px] text-center text-[14px] font-semibold leading-[normal] text-[#0A0A0A]">
              {calendarMonthOptions[visibleMonthIndex].label}
            </span>
            <button
              type="button"
              onClick={() => moveVisibleMonth(1)}
              className="focus:outline-none focus-visible:ring-2 focus-visible:ring-cpaGold/40"
              style={claudeNavigationButtonStyle}
              aria-label="Next month"
            >
              ›
            </button>
            <CalendarYearDropdown
              open={yearListOpen}
              visibleYear={visibleYear}
              years={dropdownYearOptions}
              isYearDisabled={isYearOutsideDateBounds}
              onOpenChange={setYearListOpen}
              onSelect={setVisibleYear}
            />
          </div>
        ) : (
        <div className="flex items-center justify-between gap-[6px]">
          <button
            type="button"
            onClick={() => moveVisibleMonth(-1)}
            className="focus:outline-none focus-visible:ring-2 focus-visible:ring-cpaGold/40"
            style={claudeNavigationButtonStyle}
            aria-label="Previous month"
          >
            ‹
          </button>
          <div className="flex min-w-0 items-center justify-center gap-[6px]">
            <span className="text-[14px] font-semibold text-[#0A0A0A]">
              {calendarMonthOptions[visibleMonthIndex].label}
            </span>
            <button
              type="button"
              aria-label="Previous year"
              onClick={() => setVisibleYear((year) => year - 1)}
              className="focus:outline-none focus-visible:ring-2 focus-visible:ring-cpaGold/40"
              style={{ ...claudeNavigationButtonStyle, width: 22, height: 22, fontSize: 13 }}
            >
              ‹
            </button>
            <span className="text-[14px] font-semibold text-[#0A0A0A]">{visibleYear}</span>
            <button
              type="button"
              aria-label="Next year"
              onClick={() => setVisibleYear((year) => year + 1)}
              className="focus:outline-none focus-visible:ring-2 focus-visible:ring-cpaGold/40"
              style={{ ...claudeNavigationButtonStyle, width: 22, height: 22, fontSize: 13 }}
            >
              ›
            </button>
          </div>
          <button
            type="button"
            onClick={() => moveVisibleMonth(1)}
            className="focus:outline-none focus-visible:ring-2 focus-visible:ring-cpaGold/40"
            style={claudeNavigationButtonStyle}
            aria-label="Next month"
          >
            ›
          </button>
        </div>
        )}
        <div className="grid grid-cols-7 gap-[2px]">
          {weekdayCells}
          {dayCells}
        </div>
      </>
    );
  };

  const isMonthDay = mode === "monthDay";
  const calendarPopover = open && (
    <div
      ref={popoverRef}
      id={calendarPopoverId}
      role="dialog"
      aria-label={`${ariaLabel} calendar`}
      className={isInlineCalendar
        ? "flex flex-col gap-[10px]"
        : `fixed z-[3000] flex flex-col overflow-y-auto bg-white ${
            isMonthDay
              ? "gap-[10px] rounded-[12px] border border-[#ECECEC] p-[16px]"
              : "gap-[10px] rounded-xl border border-[#9E9E9E] p-[14px]"
          }`}
      style={isInlineCalendar ? undefined : {
        ...popoverPosition,
        boxShadow: isMonthDay
          ? "0 8px 24px rgba(0,0,0,.12)"
          : "0 10px 28px rgba(0,0,0,.14)",
        boxSizing: "border-box",
        maxHeight: "calc(100vh - 16px)",
        maxWidth: "calc(100vw - 16px)",
        // The open year list may extend past the month grid (design list is
        // 200px), so the panel must not clip it.
        overflowY: yearListOpen ? "visible" : undefined,
        visibility: popoverPosition ? "visible" : "hidden",
        width: isMonthDay ? 300 : 280,
      }}
      onKeyDown={handleEscapeKey}
    >
      {isMonthDay ? renderMonthDayCalendar() : renderFullDateCalendar()}
    </div>
  );
  const portalTarget = isInlineCalendar && calendarContainer
    ? calendarContainer
    : portalContainer ?? document.body;
  const iconButtonStyle: React.CSSProperties =
    iconPosition === "left" ? { left: 0 } : { right: 0 };
  const iconPadding: React.CSSProperties =
    iconPosition === "left"
      ? { paddingLeft: 38, paddingRight: 14 }
      : { paddingRight: 38 };

  return (
    <div ref={rootRef} className={`relative ${className}${isEmptyFullDate ? ` ${placeholderClassName}` : ""}`}>
      {mode === "fullDate" ? (
        <>
          <input
            ref={inputRef}
            id={id}
            type="text"
            role="combobox"
            autoComplete="off"
            aria-autocomplete="none"
            aria-label={ariaLabel}
            aria-invalid={ariaInvalid ?? (showError || undefined)}
            aria-describedby={describedBy}
            aria-controls={open ? calendarPopoverId : undefined}
            aria-expanded={open}
            aria-haspopup="dialog"
            disabled={disabled}
            value={outOfRangeText ?? (isEmptyFullDate ? "" : displayValue)}
            placeholder={props.placeholder || "Select Date"}
            className={`${inputClassName} placeholder:text-inherit`}
            style={{
              color: isEmptyFullDate && outOfRangeText === null ? "inherit" : undefined,
              ...iconPadding,
              ...errorBorderStyle,
            }}
            onFocus={handleOpen}
            onClick={handleOpen}
            onChange={handleTextChange}
            onBlur={() => setOutOfRangeText(null)}
            onKeyDown={(event) => {
              if (handleEscapeKey(event)) return;
              if (event.key === "ArrowDown") {
                event.preventDefault();
                handleOpen();
                const selectedDay = popoverRef.current?.querySelector<HTMLButtonElement>(
                  'button[aria-pressed="true"]:not(:disabled)',
                );
                const firstEnabledDay = popoverRef.current?.querySelector<HTMLButtonElement>(
                  'button[aria-pressed]:not(:disabled)',
                );
                (selectedDay ?? firstEnabledDay)?.focus();
              }
            }}
          />
          <button
            type="button"
            aria-label={`Toggle ${ariaLabel} calendar`}
            disabled={disabled}
            tabIndex={-1}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => open ? setOpen(false) : handleOpen()}
            className="absolute top-1/2 flex h-[41px] w-[40px] -translate-y-1/2 items-center justify-center text-[#727272]"
            style={iconButtonStyle}
          >
            {calendarIcon}
          </button>
        </>
      ) : (
        <button
          ref={triggerRef}
          type="button"
          id={id}
          aria-label={ariaLabel}
          aria-describedby={describedBy}
          aria-controls={open ? calendarPopoverId : undefined}
          aria-expanded={open}
          aria-haspopup="dialog"
          disabled={disabled}
          style={{ ...iconPadding, ...errorBorderStyle }}
          onClick={() => {
            if (open) {
              setOpen(false);
            } else {
              handleOpen();
            }
          }}
          onKeyDown={(event) => {
            handleEscapeKey(event);
          }}
          className={`${inputClassName} relative text-left`}
        >
          <span className={`block truncate ${isEmptyFullDate ? placeholderClassName : ""}`}>{displayValue}</span>
          <span
            aria-hidden="true"
            className="pointer-events-none absolute top-1/2 flex w-[40px] -translate-y-1/2 items-center justify-center text-[#727272]"
            style={iconButtonStyle}
          >
            {calendarIcon}
          </span>
        </button>
      )}
      {shownErrorMessage ? (
        <p id={errorMessageId} className="mt-[6px] text-[12px] leading-[16px] text-[#E24C4C]">
          {shownErrorMessage}
        </p>
      ) : null}
      {calendarPopover ? createPortal(calendarPopover, portalTarget) : null}
    </div>
  );
};

export default CalendarDateInput;
